import { ComboboxMultiple, type ComboboxMultipleProps } from '@eye-able/ui';
import { useSearchAdmin } from '@/http/queries';
import type { AuditData } from '@eye-able/types';
import { useAtom, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { availableAuditEntriesAtom } from '@/global-store';
import { getShortUrl } from '@helpers/shorten-url';
import { useLimitedComboboxItems } from '@hooks/useLimitedComboboxItems';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export type DomainOption = {
  id: number;
  value: string;
  label: string;
  /** Full domain URL, shown under the alias in the option list. */
  domain: string;
  domainAlias?: string;
  /** Matched by the combobox search so a domain is found by alias or URL. */
  searchText?: string;
  /** Source data retained when the option was found by an admin search. */
  auditEntry?: AuditData;
};

/**
 * The domains the current user can pick from, sourced from the global store and
 * shaped into combobox options. Centralized here so consumers don't each repeat
 * the atom read and mapping.
 */
export function useDomainOptions(): DomainOption[] {
  const [availableAuditEntries] = useAtom(availableAuditEntriesAtom);
  return useMemo(
    () =>
      availableAuditEntries.map((entry) => ({
        id: entry.id,
        value: String(entry.id),
        label: entry.domainAlias || entry.domain,
        domain: entry.domain,
        domainAlias: entry.domainAlias || undefined,
        searchText: entry.domainAlias
          ? `${entry.domainAlias} ${entry.domain}`
          : entry.domain,
        auditEntry: entry,
      })),
    [availableAuditEntries],
  );
}

interface FormDomainSelectProps {
  /** Selected domain ids. */
  value: number[];
  onValueChange: (ids: number[]) => void;
  /**
   * Domains to choose from. Defaults to every domain the user has access to, so
   * most callers can omit it.
   */
  options?: DomainOption[];
  /** Field label. */
  label?: string;
  /** Trigger placeholder shown when nothing is selected. */
  placeholder?: string;
  /** Marks the field as invalid (e.g. when no domain is selected on submit). */
  invalid?: boolean;
  /**
   * Slot overrides forwarded to the underlying `ComboboxMultiple`, e.g. to
   * style the label or trigger. Merged with the component's own slot props;
   * the invalid state always wins on the trigger.
   */
  slotProps?: ComboboxMultipleProps<DomainOption>['slotProps'];
}

/**
 * Reusable multi-select for domains. It fetches the available domains itself
 * (override via `options`) and works in domain ids, so callers don't manage the
 * option list or resolve ids into option objects.
 *
 * Accounts can have thousands of domains, so rendering the full option list
 * lags the combobox. This caps the rendered options via `useLimitedComboboxItems`
 * (type-ahead past the threshold), the same approach the report `DomainSelect`
 * uses.
 */
export function FormDomainSelect({
  value,
  onValueChange,
  options,
  label,
  placeholder,
  invalid,
  slotProps,
}: FormDomainSelectProps) {
  const { t } = useInlineTranslation();
  const setAvailableAuditEntries = useSetAtom(availableAuditEntriesAtom);
  const availableOptions = useDomainOptions();
  const [searchValue, setSearchValue] = useState('');
  // The license bootstrap only contains a bounded domain window for admins.
  // Match the report selector by fetching additional domains as they type.
  const searchedAuditEntries = useSearchAdmin(
    'reportEntries',
    options === undefined ? searchValue : '',
    300,
  ).data as AuditData[] | undefined;
  const searchedOptions = useMemo(
    () =>
      (searchedAuditEntries ?? []).map((entry) => ({
        id: entry.id,
        value: String(entry.id),
        label: entry.domainAlias || entry.domain,
        domain: entry.domain,
        domainAlias: entry.domainAlias || undefined,
        searchText: entry.domainAlias
          ? `${entry.domainAlias} ${entry.domain}`
          : entry.domain,
        auditEntry: entry,
      })),
    [searchedAuditEntries],
  );
  const resolvedOptions = useMemo(
    () => options ?? mergeDomainOptionsById(availableOptions, searchedOptions),
    [availableOptions, options, searchedOptions],
  );

  const selectedIds = useMemo(() => new Set(value), [value]);
  // Filtered from `resolvedOptions` so the selected items share their object
  // references — that's how ComboboxMultiple matches selection by default.
  const selectedOptions = useMemo(
    () => resolvedOptions.filter((option) => selectedIds.has(option.id)),
    [resolvedOptions, selectedIds],
  );
  const selectedValues = useMemo(
    () => selectedOptions.map((option) => option.value),
    [selectedOptions],
  );

  const { comboboxItems } = useLimitedComboboxItems({
    items: resolvedOptions,
    searchValue,
    selectedValues,
  });

  return (
    <ComboboxMultiple
      label={label}
      items={resolvedOptions}
      filteredItems={comboboxItems}
      inputValue={searchValue}
      onInputValueChange={setSearchValue}
      value={selectedOptions}
      onValueChange={(next) => {
        const selectedSearchEntries = next
          .map((option) => option.auditEntry)
          .filter((entry): entry is AuditData => entry !== undefined);

        if (selectedSearchEntries.length) {
          setAvailableAuditEntries((currentEntries) => {
            const knownIds = new Set(currentEntries.map((entry) => entry.id));
            const newEntries = selectedSearchEntries.filter(
              (entry) => !knownIds.has(entry.id),
            );

            return newEntries.length
              ? [...currentEntries, ...newEntries]
              : currentEntries;
          });
        }

        onValueChange(next.map((option) => option.id));
      }}
      itemToStringLabel={(item) => item.label}
      itemToStringValue={(item) => item.value}
      renderChipLabel={(item) => item.label}
      renderItemLabel={(item) =>
        item.domainAlias ? (
          <div className='eye:min-w-0 eye:break-all eye:line-clamp-3'>
            <div>{item.domainAlias}</div>
            <div className='eye:text-12'>{getShortUrl(item.domain, 60)}</div>
          </div>
        ) : (
          item.label
        )
      }
      slotProps={{
        ...slotProps,
        trigger: {
          showClear: true,
          ...slotProps?.trigger,
          'aria-invalid': invalid || undefined,
        },
        value: {
          placeholder,
          ...slotProps?.value,
        },
        searchInput: {
          placeholder: t('searchPlaceholder'),
          ...slotProps?.searchInput,
        },
      }}
    />
  );
}

function mergeDomainOptionsById(
  availableOptions: DomainOption[],
  searchedOptions: DomainOption[],
): DomainOption[] {
  if (!searchedOptions.length) return availableOptions;

  const knownIds = new Set(availableOptions.map((option) => option.id));
  return [
    ...availableOptions,
    ...searchedOptions.filter((option) => !knownIds.has(option.id)),
  ];
}
